import {
  Search, Bot, Wrench, Eye, BarChart3, Download, Zap, Image as ImageIcon,
  BookOpen, GitCompare, Shield, ArrowRight, Sparkles, Lock,
} from "lucide-react";

/**
 * Post-login showcase of ALL services.
 * - First block: FREE tools — always clickable, no pricing.
 * - Second block: PAID add-ons with per-month price + "Upgrade" CTA badges.
 *
 * This component replaces the old flat "Quick Actions" grid so users see the
 * full product catalogue immediately after login (the biggest conversion
 * window we have). Badges + colors guide the eye from free → paid.
 */
export default function ServicesShowcase({ t, user }) {
  const plan = (user?.plan || "free").toLowerCase();
  const hasPaid = ["pro", "business", "enterprise", "scale"].includes(plan);

  const freeServices = [
    { key: "scan", icon: Search, href: "/",
      label: t.svc_scan_label || "New SEO Scan",
      desc: t.svc_scan_desc || "Instant SEO/GEO scan for any URL" },
    { key: "ai-sim", icon: Bot, href: "/ai-simulator",
      label: t.svc_aisim_label || "AI Simulator",
      desc: t.svc_aisim_desc || "See how ChatGPT / Gemini answer about you" },
    { key: "fix", icon: Wrench, href: "/fix-my-seo",
      label: t.svc_fix_label || "Fix My SEO",
      desc: t.svc_fix_desc || "Copy-paste fixes for your code" },
    { key: "plugin", icon: Download, href: "/plugin",
      label: t.svc_plugin_label || "WordPress Plugin",
      desc: t.svc_plugin_desc || "1-click install on your site" },
    { key: "cookie", icon: Shield, href: "/cookie-audit",
      label: t.svc_cookie_label || "Cookie / GDPR Audit",
      desc: t.svc_cookie_desc || "Compliance health check" },
    { key: "local-seo", icon: Search, href: "/seo/en",
      label: t.svc_local_label || "Local SEO pages",
      desc: t.svc_local_desc || "500+ city landing pages" },
  ];

  const paidServices = [
    { key: "ads-creator", icon: ImageIcon, href: "/ads-creator",
      label: "PulseStudio", price: "€69/mo",
      desc: t.svc_ads_desc || "AI reel director, Photo → Ad, multi-scene video editor",
      accent: "from-fuchsia-500 to-purple-600", hot: true },
    { key: "brand", icon: Eye, href: "/brand-monitor",
      label: "Brand Monitor", price: "€19/mo",
      desc: t.svc_brand_desc || "Track how ChatGPT, Gemini & Perplexity mention you",
      accent: "from-purple-500 to-indigo-600" },
    { key: "competitor", icon: GitCompare, href: "/products/competitor-spy",
      label: "Competitor Spy", price: "€29/mo",
      desc: t.svc_cs_desc || "Daily alerts when competitors rank new keywords",
      accent: "from-rose-500 to-pink-600" },
    { key: "attribution", icon: BarChart3, href: "/attribution",
      label: "AI Attribution", price: "€39/mo",
      desc: t.svc_attr_desc || "See which AI engine brought each sale",
      accent: "from-orange-500 to-rose-600" },
    { key: "autopilot", icon: Zap, href: "/products/seo-autopilot",
      label: "SEO Autopilot", price: "€79/mo",
      desc: t.svc_auto_desc || "Auto-detect and auto-fix SEO issues 24/7",
      accent: "from-amber-500 to-orange-600" },
    { key: "blog-agent", icon: BookOpen, href: "/products/blog-agent",
      label: "Blog Agent", price: "€25",
      desc: t.svc_blog_desc || "AI-written SEO blog posts, one-time pack",
      accent: "from-emerald-500 to-teal-600" },
  ];

  return (
    <div className="space-y-8" data-testid="services-showcase">
      {/* ----- FREE tools ----- */}
      <section data-testid="services-free">
        <div className="flex items-center gap-3 mb-4">
          <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider rounded bg-emerald-100 text-emerald-700">
            {t.svc_free_badge || "FREE"}
          </span>
          <h3 className="font-bold text-lg font-['Chivo']">
            {t.svc_free_title || "Free tools included with your account"}
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {freeServices.map((s) => (
            <a
              key={s.key}
              href={s.href}
              data-testid={`services-free-${s.key}`}
              className="flex items-start gap-3 p-4 bg-emerald-50/40 hover:bg-emerald-50 border border-emerald-200/60 transition-colors group rounded"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-500/10 rounded">
                <s.icon className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-bold text-sm">{s.label}</p>
                  <span className="text-[9px] font-black tracking-wider text-emerald-600">FREE</span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5">{s.desc}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
            </a>
          ))}
        </div>
      </section>

      {/* ----- PAID tools ----- */}
      <section data-testid="services-paid">
        <div className="flex items-center gap-3 mb-2">
          <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider rounded bg-purple-100 text-purple-700">
            PRO
          </span>
          <h3 className="font-bold text-lg font-['Chivo']">
            {t.svc_paid_title || "Premium add-ons"}
          </h3>
          {!hasPaid && (
            <a
              href="/#pricing"
              data-testid="services-upgrade-banner"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white text-xs font-bold shadow-sm hover:shadow-md transition-shadow"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {t.svc_upgrade || "Upgrade for all features"}
            </a>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-4">
          {t.svc_paid_sub || "Unlock advanced AI tracking, monitoring and automation. Cancel anytime."}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {paidServices.map((s) => (
            <a
              key={s.key}
              href={s.href}
              data-testid={`services-paid-${s.key}`}
              className="relative p-4 bg-white hover:shadow-lg border border-gray-200 rounded transition-all group overflow-hidden"
            >
              {s.hot && (
                <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[9px] font-black tracking-wider text-white bg-gradient-to-r from-fuchsia-500 to-purple-600 rounded">
                  HOT
                </span>
              )}
              <div className={`w-10 h-10 mb-3 flex items-center justify-center bg-gradient-to-br ${s.accent} rounded-lg shadow-sm`}>
                <s.icon className="w-5 h-5 text-white" />
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <p className="font-bold text-sm">{s.label}</p>
                <span className="text-[11px] font-mono text-purple-700 font-bold">{s.price}</span>
              </div>
              <p className="text-xs text-gray-600 mb-3 min-h-[32px]">{s.desc}</p>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-purple-700 group-hover:gap-1.5 transition-all">
                {hasPaid ? (t.svc_open || "Open") : (t.svc_unlock || "Unlock")}
                {!hasPaid && <Lock className="w-3 h-3" />}
                <ArrowRight className="w-3 h-3" />
              </span>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
